import isEqual from 'lodash/isEqual';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

export interface UseFormValueOptions<TValue> {
  name: string;
  propValue?: TValue;
  emptyValue: TValue;
  defaultValue?: TValue;
  onChange: (fieldName: string, newValue: any, newDirtyState?: boolean) => void;
  /** for doing side effects or tighter control on the useEffect that updates the local state from props */
  onPropUpdated?: (
    propOrEmpty: TValue,
    currentValue: TValue,
    setValue: Dispatch<SetStateAction<TValue>>
  ) => void;
}

export const useFormValue = <TValue>({
  name,
  propValue,
  emptyValue,
  onChange,
  defaultValue,
  onPropUpdated,
}: UseFormValueOptions<TValue>) => {
  const getPropOrEmpty = useCallback(() => {
    return propValue === undefined ? emptyValue : propValue;
  }, [emptyValue, propValue]);

  const [value, setValue] = useState(defaultValue || getPropOrEmpty());

  // if we receive an empty value, tell the form the initial value
  useEffect(() => {
    if (propValue === undefined && !isEqual(propValue, value)) {
      const initialValue = defaultValue !== undefined ? defaultValue : getPropOrEmpty();
      onChange(name, initialValue, false);
    }
    // we only want to do this on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextValue = useRef(value);

  useEffect(() => {
    const prop = getPropOrEmpty();
    if (onPropUpdated) {
      onPropUpdated(prop, value, setValue);
    } else {
      // defaults to isEqual check
      // prevents undefined values from causing a rerender
      if (!isEqual(prop, value)) {
        setValue(prop);
        nextValue.current = prop;
      }
    }
    // only do this when the prop value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  useEffect(() => {
    if (!isEqual(getPropOrEmpty(), nextValue.current)) {
      onChange(name, value);
    }
    // only do this when the state value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, value, name]);
  nextValue.current = value;

  return [value, setValue] as [TValue, Dispatch<SetStateAction<TValue>>];
};
